import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import SEO from "@/components/marketing/SEO";

const Sion = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Piktable Sion",
    "description": "Réservations en ligne pour restaurants à Sion",
    "url": "https://piktable.ch/sion",
    "areaServed": {
      "@type": "City",
      "name": "Sion",
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": "Canton du Valais"
      }
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "46.2334",
      "longitude": "7.3588"
    },
    "priceRange": "CHF 10-99",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Sion",
      "addressCountry": "CH"
    },
    "email": "contact@piktable.ch",
    "sameAs": ["https://www.instagram.com/piktable"],
    "image": "https://piktable.ch/screenshots/dashboard-overview.png"
  };

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://piktable.ch/" },
      { "@type": "ListItem", "position": 2, "name": "Sion", "item": "https://piktable.ch/sion" }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Réservations en ligne pour restaurants à Sion"
        description="Piktable aide les restaurants valaisans à gérer leurs réservations simplement. Solution suisse romande adaptée aux établissements de Sion."
        canonical="/sion"
        structuredData={[structuredData, breadcrumbData]}
      />
      <Header />
      <main className="pt-16">
        <section className="py-24 bg-gradient-hero">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center space-y-8">
              <h1 className="font-playfair text-5xl md:text-6xl font-bold text-foreground">
                Réservations en ligne pour restaurants à Sion
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Piktable aide les restaurants valaisans à gérer leurs réservations simplement. Solution suisse romande adaptée aux établissements de Sion et du Valais.
              </p>
              <div className="flex justify-center pt-6">
                <Button size="lg" onClick={() => navigate("/signup")}>
                  Essayer gratuitement
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto space-y-12">
              <div className="text-center">
                <h2 className="font-playfair text-4xl md:text-5xl font-bold text-foreground mb-6">
                  Piktable pour les restaurants sédunois
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">🍷</div>
                  <h3 className="text-2xl font-bold text-foreground">Adapté au Valais</h3>
                  <p className="text-muted-foreground">
                    Solution pensée pour les restaurants valaisans, des bistrots de ville aux établissements gastronomiques.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">📱</div>
                  <h3 className="text-2xl font-bold text-foreground">Réservation mobile</h3>
                  <p className="text-muted-foreground">
                    Widget de réservation optimisé pour smartphone. Vos clients réservent facilement depuis leur mobile.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">🔄</div>
                  <h3 className="text-2xl font-bold text-foreground">Gestion flexible</h3>
                  <p className="text-muted-foreground">
                    Créneaux horaires adaptables, combinaison de tables, gestion de groupes. Piktable s'adapte à votre service.
                  </p>
                </div>

                <div className="space-y-4 p-6 rounded-xl bg-card border border-border">
                  <div className="text-4xl mb-2">🎯</div>
                  <h3 className="text-2xl font-bold text-foreground">Sans engagement</h3>
                  <p className="text-muted-foreground">
                    Jusqu'à 60 jours gratuits pour tester, puis 49 CHF/mois tout inclus, ou 29 CHF à vie pour les 50 fondateurs du Printemps 2026. Zéro commission, pas de contrat longue durée.
                  </p>
                </div>
              </div>

              <div className="mt-12 space-y-4">
                <h3 className="font-playfair text-2xl font-bold text-foreground">
                  Sion : au coeur du Valais viticole
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Sion est le coeur du Valais, une région où la restauration est intimement liée au terroir et à l'oenotourisme. Les restaurants sédunois accueillent des amateurs de vins, des randonneurs de passage et des familles locales — souvent avec des pics de fréquentation liés aux saisons touristiques et aux événements viticoles. De la vieille ville historique à Bramois, en passant par Conthey et les villages environnants, les restaurateurs valaisans ont besoin d'un outil simple qui ne complique pas leur quotidien. Piktable permet de gérer les réservations de groupes pour des dégustations, d'adapter les créneaux aux services du midi et du soir, et d'offrir une expérience de réservation digne des établissements du terroir valaisan.
                </p>
              </div>

              <div className="mt-16 p-8 rounded-2xl bg-primary/5 border-2 border-primary/20">
                <h3 className="font-playfair text-3xl font-bold text-foreground mb-4 text-center">
                  Idéal pour votre restaurant sédunois
                </h3>
                <p className="text-lg text-muted-foreground text-center mb-8">
                  Du centre historique aux quartiers modernes de Sion, Piktable s'adapte à tous les types d'établissements.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" className="w-full sm:w-auto" onClick={() => navigate("/signup")}>
                    Démarrer gratuitement
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="w-full sm:w-auto"
                  >
                    En savoir plus
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Sion;
